import { useEffect, useMemo, useRef, useState } from 'react';
import {
  renderSectionPreview,
  type SectionDocument,
  type SectionEditModel,
  type SectionSlotView,
  type SectionsView,
} from '@nopain-newsletter/core/sections';
import { MOCK_MODE } from '../../lib/api';
import { mockMediaPreviewSrc } from '../../lib/mock/rehost';
import {
  editorDocument,
  hitAt,
  holdsRender,
  mapImageSources,
  markSelection,
  patchSlot,
  reveal,
} from './canvasDom';

export type CanvasDevice = 'desktop' | 'mobile';

/** Width the mail is laid out at in the desktop view; a narrower screen shows it scaled down. */
const DESKTOP_WIDTH = 640;
const MOBILE_WIDTH = 390;

let nextRenderId = 1;

function displaySrc(src: string): string {
  return MOCK_MODE ? (mockMediaPreviewSrc(src) ?? src) : src;
}

/**
 * The mail in the middle of the section editor. The frame is rendered
 * again only when the layout changes (move, hide, duplicate, remove,
 * merge, split) or another original arrives. A changed text, image or
 * link is written straight into the frame, so typing does not reload a
 * mail that can be several megabytes.
 */
export function SectionCanvas({
  doc,
  original,
  model,
  view,
  device,
  selectedSection,
  selectedSlot,
  onSelect,
  onKeyDown,
}: {
  doc: SectionDocument;
  original: string;
  model: SectionEditModel;
  view: SectionsView;
  device: CanvasDevice;
  selectedSection: string | null;
  selectedSlot: string | null;
  onSelect: (sectionId: string | null, slotId: string | null) => void;
  /** Keys pressed while the focus is inside the mail (for the editor's shortcuts). */
  onKeyDown?: (event: KeyboardEvent) => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const modelRef = useRef(model);
  modelRef.current = model;
  const handlers = useRef({ onSelect, onKeyDown });
  handlers.current = { onSelect, onKeyDown };

  const layout = model.layout;
  const rendered = useMemo(
    () => {
      const current = modelRef.current;
      const id = nextRenderId++;
      return {
        id,
        srcDoc: editorDocument(renderSectionPreview(doc, original, current).html, id),
        slots: current.slots ?? {},
      };
    },
    // Slot edits are patched in below; only the layout needs a new render.
    [doc, original, layout],
  );

  /** Counts loads of the frame, so the effects below run again for a new document. */
  const [loaded, setLoaded] = useState(0);
  const loadedFor = useRef<typeof rendered | null>(null);
  const applied = useRef<NonNullable<SectionEditModel['slots']>>({});

  const slotsById = useMemo(() => {
    const map = new Map<string, SectionSlotView>();
    for (const section of view.sections) for (const slot of section.slots) map.set(slot.id, slot);
    return map;
  }, [view]);

  /** Where the mail was scrolled to, kept across a new render so a move does not jump to the top. */
  const scrollY = useRef(0);
  const restored = useRef<{ wanted: number; got: number } | null>(null);

  const ready = (frameDoc: Document) => {
    if (loadedFor.current === rendered) return;
    loadedFor.current = rendered;
    applied.current = rendered.slots;
    if (MOCK_MODE) mapImageSources(frameDoc, displaySrc);
    const wanted = scrollY.current;
    const win = frameDoc.defaultView;
    if (wanted > 0 && win) {
      win.scrollTo(0, wanted);
      restored.current = { wanted, got: win.scrollY };
    } else {
      restored.current = null;
    }
    setLoaded((n) => n + 1);
  };
  const readyRef = useRef(ready);
  readyRef.current = ready;

  // The load event waits for every image. The mail can be used as soon as it is parsed.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (loadedFor.current === rendered) {
        window.clearInterval(timer);
        return;
      }
      const frameDoc = frame.current?.contentDocument;
      if (holdsRender(frameDoc, rendered.id)) {
        window.clearInterval(timer);
        readyRef.current(frameDoc);
      }
    }, 30);
    return () => window.clearInterval(timer);
  }, [rendered]);

  const onLoad = () => {
    const frameDoc = frame.current?.contentDocument;
    if (!frameDoc) return;
    if (loadedFor.current !== rendered) {
      ready(frameDoc);
      return;
    }
    // The images have their height now: finish a scroll that stopped short, and show the selection.
    const win = frameDoc.defaultView;
    const kept = restored.current;
    if (win && kept && kept.got < kept.wanted && Math.abs(win.scrollY - kept.got) < 2) {
      win.scrollTo(0, kept.wanted);
    }
    restored.current = null;
    setLoaded((n) => n + 1);
  };

  // Clicks and keys inside the mail. Links never navigate: a click selects.
  useEffect(() => {
    const frameDoc = frame.current?.contentDocument;
    if (!frameDoc || loaded === 0) return;
    const onClick = (event: Event) => {
      event.preventDefault();
      const hit = hitAt(event.target);
      handlers.current.onSelect(hit.sectionId, hit.slotId);
    };
    const onKey = (event: Event) => handlers.current.onKeyDown?.(event as KeyboardEvent);
    const win = frameDoc.defaultView;
    const onScroll = () => {
      scrollY.current = win?.scrollY ?? 0;
    };
    frameDoc.addEventListener('click', onClick, true);
    frameDoc.addEventListener('keydown', onKey);
    win?.addEventListener('scroll', onScroll);
    return () => {
      frameDoc.removeEventListener('click', onClick, true);
      frameDoc.removeEventListener('keydown', onKey);
      win?.removeEventListener('scroll', onScroll);
    };
  }, [loaded]);

  // Slot edits since the frame was rendered: write each changed one into the frame.
  useEffect(() => {
    const frameDoc = frame.current?.contentDocument;
    if (!frameDoc || loadedFor.current !== rendered) return;
    const before = applied.current;
    const now = model.slots ?? {};
    if (before === now) return;
    for (const id of new Set([...Object.keys(before), ...Object.keys(now)])) {
      if (before[id] === now[id]) continue;
      const slot = slotsById.get(id);
      if (slot) patchSlot(frameDoc, slot, displaySrc);
    }
    applied.current = now;
  }, [model, slotsById, rendered, loaded]);

  useEffect(() => {
    const frameDoc = frame.current?.contentDocument;
    if (!frameDoc || loaded === 0) return;
    const target = markSelection(frameDoc, selectedSection, selectedSlot);
    if (target) reveal(target, selectedSlot !== null ? 'center' : 'start');
  }, [selectedSection, selectedSlot, loaded]);

  // The desktop view on a narrow screen: the mail keeps its width and is scaled to fit.
  const [fit, setFit] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => setFit({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const wanted = device === 'mobile' ? MOBILE_WIDTH : DESKTOP_WIDTH;
  const scale = fit && fit.width > 0 && fit.width < wanted ? fit.width / wanted : 1;
  const frameStyle =
    scale < 1 && fit
      ? {
          width: wanted,
          height: fit.height / scale,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }
      : device === 'mobile'
        ? { width: MOBILE_WIDTH, height: '100%' }
        : { width: '100%', height: '100%' };

  return (
    <div
      ref={box}
      className="relative h-full min-h-0 w-full overflow-hidden"
      data-device={device}
      data-scale={scale < 1 ? scale.toFixed(3) : '1'}
    >
      <iframe
        ref={frame}
        title="Mail"
        sandbox="allow-same-origin"
        srcDoc={rendered.srcDoc}
        onLoad={onLoad}
        style={frameStyle}
        className={
          device === 'mobile' && scale === 1
            ? 'mx-auto block rounded-2xl border border-border bg-white shadow-sm'
            : 'block border-0 bg-white'
        }
      />
    </div>
  );
}
